// FILE: src/config/badges.js
// Canonical, data-only configuration for the home-page badge system.
//
// This file is the SINGLE SOURCE OF TRUTH for:
//   • BADGE_TYPES          — the three badge classes
//   • BADGE_COLORS         — category → color language (centralized here so the
//                            component never hardcodes a hex value)
//   • BADGE_PLACEMENT      — which card corner each card-level type pins to
//   • CARD_BADGE_MODE      — 'dual' (Variant A) vs 'single' (Variant B)
//   • BADGE_ROTATION_CONFIG— platform-row rotation cadence
//   • BADGE_TRIGGER_RULES  — telemetry thresholds (a badge must be EARNED)
//   • BADGES               — the badge registry (the catalog of every badge)
//
// It contains NO React and NO logic beyond trivial lookups — selection,
// rotation, prioritization and telemetry filtering all live in
// src/services/badgeService.js. Components read the resolved result, never this
// file directly (except BADGE_COLORS via the service helper).
//
// Adding / changing a badge is a data edit here — no component changes needed.
//
// ── Badge classes ────────────────────────────────────────────────────────────
//   1. PRODUCT   — certification / content status. Mostly static. GREEN.
//                  Max 1 per CTA card. e.g. "PMBOK 8 Ready", "New Questions".
//   2. PLATFORM  — PM Pixel functionality. Rotating, one visible at a time.
//                  BLUE. Lives in its own row ABOVE the cert cards.
//   3. TELEMETRY — learner activity / social proof. Dynamic. GOLD. Renders only
//                  when its trigger condition is met against a live telemetry
//                  snapshot — never hardcoded marketing copy.

export const BADGE_TYPES = {
  PRODUCT: 'product',
  PLATFORM: 'platform',
  TELEMETRY: 'telemetry',
};

// ── Color system ─────────────────────────────────────────────────────────────
// Category → token set. The component applies these as CSS custom properties so
// the colour mapping lives ONLY here. `neutral` is a non-category fallback used
// by status badges that should read as quiet (e.g. "Coming Soon"). Values are
// light, premium tints with a darker same-family text colour (never pure black)
// to match the white/light interface.
export const BADGE_COLORS = {
  [BADGE_TYPES.PRODUCT]:   { bg: '#e3f7ec', border: '#8ed7a6', text: '#15663a', dot: '#8ed7a6' },
  [BADGE_TYPES.PLATFORM]:  { bg: '#e6f1fb', border: '#85b7eb', text: '#0c447c', dot: '#85b7eb' },
  [BADGE_TYPES.TELEMETRY]: { bg: '#fff3e0', border: '#f0b343', text: '#95591a', dot: '#ef9f27' },
  neutral:                 { bg: '#eef1f6', border: '#c9d3e2', text: '#54627a', dot: '#c9d3e2' },
};

// ── Card-level placement ─────────────────────────────────────────────────────
// Where each card-level badge type pins:
//   'emblem'    — angled sticker overlapping the round cert logo (the premium
//                 "sticker slapped on the emblem" look, consistent per card)
//   'top-right' — straddling the card's top-right corner
// Product pins to the emblem; telemetry takes the top-right corner so the two
// never collide. Platform badges are not card-level — they render in the
// rotating row in the hero — so they have no entry here.
export const BADGE_PLACEMENT = {
  [BADGE_TYPES.PRODUCT]:   'emblem',
  [BADGE_TYPES.TELEMETRY]: 'top-right',
};

// 'dual'   → Variant A: product (top-right) AND telemetry (top-left) may both show.
// 'single' → Variant B: one badge per card in the top-right, chosen by priority
//            (Product > Telemetry). Flip this one value to switch the whole UI.
export const CARD_BADGE_MODE = 'dual';

// ── Platform-row rotation ────────────────────────────────────────────────────
export const BADGE_ROTATION_CONFIG = {
  intervalMs: 24000,   // 24s — within the 20–30s spec band
  visibleCount: 1,     // max 1 rotating badge visible at a time
  pauseOnHover: true,  // hovering the row pauses the rotation
  animation: 'fade',   // 'fade' — subtle; matches the "no flashing/bouncing" rule
};

// ── Telemetry trigger rules ──────────────────────────────────────────────────
// Keyed by a badge's `triggerCondition`. The service evaluates `metric op value`
// against a per-cert telemetry snapshot, with optional sample-size guards so a
// badge can't be earned on thin data. Thresholds are intentionally demanding —
// these are social proof, not marketing copy.
//   metric      — field read from the telemetry snapshot
//   op          — 'gte' | 'gt' | 'lte' | 'lt' | 'eq'
//   value       — threshold compared against the metric
//   minSamples  — (optional) snapshot.sampleCount floor before the rule can pass
//   windowDays  — (optional) documentation of the measurement window
export const BADGE_TRIGGER_RULES = {
  trending:           { metric: 'sessionsGrowthPct', op: 'gte', value: 25, windowDays: 7 },
  'fast-growing':     { metric: 'sessionsGrowthPct', op: 'gte', value: 50, windowDays: 30 },
  'learner-favorite': { metric: 'avgRating', op: 'gte', value: 4.5, minSamples: 30 },
  'top-rated':        { metric: 'avgRating', op: 'gte', value: 4.7, minSamples: 50 },
  'most-used':        { metric: 'sessionsRank', op: 'eq', value: 1 },
};

// ── Badge registry ───────────────────────────────────────────────────────────
// The full catalog. `active: false` entries are ready-to-flip templates — they
// are excluded from rendering until activated. Fields:
//   id                   stable key (also the asset stem / test hook)
//   label                visible capsule text
//   type                 BADGE_TYPES value → drives colour + placement
//   priority             higher wins when a slot can show only one badge
//   active               master on/off switch
//   variant              optional colour override key in BADGE_COLORS (e.g. 'neutral')
//   alt                  accessible description (defaults to label if omitted)
//   assetKey             optional art stem under /themes/pm-pixel/badges/ for an
//                        inline glyph (the capsule renders with or without it)
//   triggerCondition     (telemetry only) key into BADGE_TRIGGER_RULES
//   certificationTargets examId[] | '*' (all). Platform/telemetry usually '*'.
export const BADGES = [
  // ── Product / certification (green, static, max 1 per card) ────────────────
  {
    id: 'pmbok8-ready',
    label: 'PMBOK 8 Ready',
    type: BADGE_TYPES.PRODUCT,
    priority: 100,
    active: true,
    assetKey: 'pmbok8-ready-badge',
    alt: 'PMP exam content aligned to the PMBOK® Guide, 8th Edition',
    certificationTargets: ['pmp'],
  },
  // PMI-ACP example (deliverable #5). Inactive on purpose: PMI-ACP already shows
  // its established inline "Coming Soon" treatment (tied to the disabled card
  // state in ExamSelector), so activating this would double-badge the card. Flip
  // `active: true` only if you retire that inline treatment in favour of the
  // corner sticker. It documents the shape of a neutral status product badge.
  {
    id: 'acp-coming-soon',
    label: 'Coming Soon',
    type: BADGE_TYPES.PRODUCT,
    priority: 90,
    active: false,
    variant: 'neutral',
    alt: 'PMI-ACP question bank is being built — coming soon',
    certificationTargets: ['pmi-acp'],
  },
  // PMI-PMOCP launch badge — green PRODUCT status sticker on the PMO-CP cert
  // card. Toggle `active` to show/hide; retarget via certificationTargets.
  // Editable like any registry badge; the round PMO-CP credential logo itself
  // is the static cert-logo asset (public/assets/cert-logos/pmocp.png), while
  // the per-cert emblem sticker is assigned in the Badge Manager admin tool.
  {
    id: 'pmi-pmocp-new',
    label: 'New',
    type: BADGE_TYPES.PRODUCT,
    priority: 88,
    active: true,
    alt: 'PMI-PMOCP track newly added to PM Pixel',
    certificationTargets: ['pmi-pmocp'],
  },
  // Ready-to-activate product templates (Bryan flips `active` + sets targets):
  { id: 'updated',       label: 'Updated',       type: BADGE_TYPES.PRODUCT, priority: 70, active: false, certificationTargets: [] },
  { id: 'new-questions', label: 'New Questions', type: BADGE_TYPES.PRODUCT, priority: 75, active: false, certificationTargets: [] },
  { id: 'now-live',      label: 'Now Live',      type: BADGE_TYPES.PRODUCT, priority: 85, active: false, certificationTargets: [] },
  { id: 'early-access',  label: 'Early Access',  type: BADGE_TYPES.PRODUCT, priority: 80, active: false, certificationTargets: [] },

  // ── Platform / feature (blue, rotating row, all certs) ─────────────────────
  { id: 'targeted-tests',       label: 'Targeted Tests',       type: BADGE_TYPES.PLATFORM, priority: 60, active: true, certificationTargets: '*' },
  { id: 'performance-insights', label: 'Performance Insights', type: BADGE_TYPES.PLATFORM, priority: 58, active: true, certificationTargets: '*' },
  { id: 'timed-exams',          label: 'Timed Exams',          type: BADGE_TYPES.PLATFORM, priority: 56, active: true, certificationTargets: '*' },
  { id: 'smart-review',         label: 'Smart Review',         type: BADGE_TYPES.PLATFORM, priority: 54, active: true, certificationTargets: '*' },
  { id: 'exam-simulator',       label: 'Exam Simulator',       type: BADGE_TYPES.PLATFORM, priority: 52, active: true, certificationTargets: '*' },
  { id: 'progress-tracking',    label: 'Progress Tracking',    type: BADGE_TYPES.PLATFORM, priority: 50, active: true, certificationTargets: '*' },

  // ── Telemetry / social proof (gold, trigger-gated, all certs) ──────────────
  // Active, but only render when their trigger fires against live telemetry.
  { id: 'most-used',        label: 'Most Used',        type: BADGE_TYPES.TELEMETRY, priority: 80, active: true, triggerCondition: 'most-used',        certificationTargets: '*' },
  { id: 'top-rated',        label: 'Top Rated',        type: BADGE_TYPES.TELEMETRY, priority: 78, active: true, triggerCondition: 'top-rated',        certificationTargets: '*' },
  { id: 'learner-favorite', label: 'Learner Favorite', type: BADGE_TYPES.TELEMETRY, priority: 76, active: true, triggerCondition: 'learner-favorite', certificationTargets: '*' },
  { id: 'trending',         label: 'Trending',         type: BADGE_TYPES.TELEMETRY, priority: 74, active: true, triggerCondition: 'trending',         certificationTargets: '*' },
  { id: 'fast-growing',     label: 'Fast Growing',     type: BADGE_TYPES.TELEMETRY, priority: 72, active: true, triggerCondition: 'fast-growing',     certificationTargets: '*' },
];

// ── Trivial lookups (no business logic — that lives in badgeService) ──────────

/** @returns {object[]} every badge with active === true. */
export function getActiveBadges() {
  return BADGES.filter((b) => b.active);
}

/** @returns {object|undefined} the badge with the given id (active or not). */
export function getBadgeById(id) {
  return BADGES.find((b) => b.id === id);
}

/**
 * Resolve the colour token set for a badge: an explicit `variant` wins, then the
 * badge `type`, then the neutral fallback. Keeps BADGE_COLORS the only source.
 * @param {object} badge
 * @returns {{bg:string,border:string,text:string,dot:string}}
 */
export function getBadgeColors(badge) {
  if (!badge) return BADGE_COLORS.neutral;
  if (badge.variant && BADGE_COLORS[badge.variant]) return BADGE_COLORS[badge.variant];
  return BADGE_COLORS[badge.type] || BADGE_COLORS.neutral;
}
